import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { useConfigurator, WIZARD_STEPS } from '../../../store/configurator';
import { MOBILE_ANIM } from '../../mobile.tokens';
import { MobileHeader } from '../../components/MobileHeader';
import { MobileInlinePreview } from '../../components/MobileInlinePreview';
import { MobileSitePreview } from '../../components/MobileSitePreview';
import { PlayIcon, ThemeCard } from '../designCards';
import {
  Screen,
  PhoneClip,
  PhoneHolder,
  Scrim,
  PreviewPill,
  PreviewPillLabel,
  Sheet,
  Tabs,
  Tab,
  Carousel,
  Cta,
} from '../MobileDesignFlow/MobileDesignFlow.styles';

type IntroPhase = 'phone' | 'cta' | 'sheet' | 'done';

const ORDER_PHASES: IntroPhase[] = ['phone', 'cta', 'sheet', 'done'];

export type MobileThemeFlowProps = {
  skipIntro: boolean;
};

/**
 * v0 mobile step 1 — Theme. Pick a base theme over a single live phone preview, with the
 * staged entrance (phone fades in, then the CTA, then the sheet). Colours + art style are
 * their own step (step 2). Split from v3's combined MobileDesignFlow.
 */
export const MobileThemeFlow = ({ skipIntro }: MobileThemeFlowProps): React.ReactElement => {
  const { t } = useTranslation();
  const { themes, selection, selectedColor, previewUrl, setTheme, saveNow } = useConfigurator();
  const navigate = useNavigate();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [phase, setPhase] = useState<IntroPhase>(skipIntro ? 'done' : 'phone');
  const [phoneIn, setPhoneIn] = useState(skipIntro);

  const timers = useRef<number[]>([]);
  useEffect(() => {
    if (skipIntro) return undefined;
    const t2 = timers.current;
    t2.push(window.setTimeout(() => setPhoneIn(true), MOBILE_ANIM.introPhoneIn));
    t2.push(window.setTimeout(() => setPhase('cta'), MOBILE_ANIM.introCta));
    t2.push(window.setTimeout(() => setPhase('sheet'), MOBILE_ANIM.introSheet));
    t2.push(window.setTimeout(() => setPhase('done'), MOBILE_ANIM.introDone));
    return () => t2.forEach((id) => window.clearTimeout(id));
  }, [skipIntro]);

  const phaseAt = (p: IntroPhase): boolean => ORDER_PHASES.indexOf(phase) >= ORDER_PHASES.indexOf(p);

  const goNext = (): void => {
    // Leaving Theme → persist (a no-op until colour + style resolve too) and advance.
    saveNow();
    navigate(WIZARD_STEPS[1].path);
  };

  return (
    <Screen>
      <MobileHeader activeStep={0} />

      <PhoneClip>
        <PhoneHolder $in={phoneIn}>
          <MobileInlinePreview />
        </PhoneHolder>
      </PhoneClip>

      <Scrim $shown={phaseAt('sheet')} />

      <PreviewPill
        type="button"
        onClick={() => setPreviewOpen(true)}
        data-ga-id="cfg-mobile-theme-preview-open"
        $shown={phaseAt('done')}
        aria-label={t('Preview')}
      >
        <PlayIcon />
        <PreviewPillLabel text="Preview" tag="span" />
      </PreviewPill>

      <Sheet $up={phaseAt('sheet')}>
        {/* Single "Theme" tab — mirrors the Colors / Art Style tab row in step 2 so the
            sheet header reads consistently across the two design steps. */}
        <Tabs>
          <Tab type="button" data-ga-id="cfg-mobile-theme-tab-themes" $active>
            <Text text="Theme" tag="span" />
          </Tab>
        </Tabs>

        <Carousel>
          {themes.map((theme) => (
            <ThemeCard key={theme.id} theme={theme} selected={theme.id === selection.themeId} onClick={() => setTheme(theme.id)} />
          ))}
        </Carousel>
      </Sheet>

      <Cta type="button" onClick={goNext} data-ga-id="cfg-mobile-theme-cta-next" $shown={phaseAt('cta')}>
        <Text text="Continue to Colors & Art Style" tag="span" />
      </Cta>

      <MobileSitePreview open={previewOpen} url={previewUrl} bgColor={selectedColor?.bg} onClose={() => setPreviewOpen(false)} />
    </Screen>
  );
};

export default MobileThemeFlow;
